import { useEffect, useRef, useState } from 'react';
import { X, Info, RefreshCw } from 'lucide-react';
import { useCalculatorStore, VARIABLE_ORDER } from '@/store/calculator';
import { VariableInput } from './VariableInput';
import { VariableType } from '@/lib/calculator';
import { cn } from '@/lib/utils';
import ReactDOM from 'react-dom';
import { Toast } from '../ui/Toast';
import { format, startOfTomorrow } from 'date-fns';

interface EditInputsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// This is the exact same scroll locking implementation used in TargetSelector
// It preserves layout by keeping the body fixed in place with the current scroll position
const useScrollLock = (isLocked: boolean) => {
  useEffect(() => {
    if (!isLocked) return;
    
    // Store original body styles
    const originalStyle = window.getComputedStyle(document.body);
    const originalOverflow = originalStyle.overflow;
    
    // Save current scroll position
    const scrollY = window.scrollY;
    
    // Apply scroll lock
    // By using position: fixed AND preserving the current scroll position with top,
    // we prevent the content from jumping AND keep the scrollbar visible
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.overflow = 'hidden';
    
    return () => {
      // Restore original styles and scroll position
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      document.body.style.overflow = originalOverflow;
      window.scrollTo(0, scrollY);
    };
  }, [isLocked]);
};

export function EditInputsModal({ isOpen, onClose }: EditInputsModalProps) {
  const { target, values, settings, updateValue, updateSetting, resetToDefaults } = useCalculatorStore();
  const [showToast, setShowToast] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  
  // Use our custom scroll lock hook - identical to TargetSelector implementation
  useScrollLock(isOpen);
  
  // Only render when open
  if (!isOpen) return null;

  const getVariableStatus = (type: VariableType) => {
    if (type === target) {
      return {
        disabled: true,
        message: 'Currently set as calculation objective',
        color: 'text-primary',
        bgColor: 'bg-primary/10',
        borderColor: 'border-primary/20'
      };
    }
    return {
      disabled: false,
      message: null,
      color: null,
      bgColor: null,
      borderColor: null
    };
  };

  const handleResetToDefaults = () => {
    resetToDefaults();
    setShowToast(true);
  };

  // Calculate min and max dates for the date picker
  const tomorrow = startOfTomorrow();
  const minDate = format(tomorrow, 'yyyy-MM-dd');
  
  // Calculate the maximum allowed date (30 years in the future)
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 30);
  const maxDateStr = format(maxDate, 'yyyy-MM-dd');

  // Handle date change
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const dateStr = e.target.value;
    
    // Convert input string YYYY-MM-DD to a Date object at UTC midnight
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
      const day = parseInt(parts[2], 10);
      const selectedDate = new Date(Date.UTC(year, month, day)); // Use UTC to avoid timezone shifts

      if (!isNaN(selectedDate.getTime())) {
        // Create today's date at UTC midnight for comparison
        const today = new Date();
        const todayUTC = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));

        // Create max date at UTC midnight
        const maxDateUTC = new Date(Date.UTC(maxDate.getUTCFullYear(), maxDate.getUTCMonth(), maxDate.getUTCDate()));

        // Ensure date is not in the past and not beyond the max date
        if (selectedDate >= todayUTC && selectedDate <= maxDateUTC) {
          // Store timestamp. Since we created the date in UTC, getTime() gives UTC timestamp
          updateValue('date', selectedDate.getTime());
        } else if (selectedDate < todayUTC) {
          // If selected date is in the past, reset to today
          updateValue('date', todayUTC.getTime());
        } else {
          // If selected date is beyond max date, reset to max date
          updateValue('date', maxDateUTC.getTime());
        }
      }
    }
  };

  // Format date for display in the native date picker
  const formatDateForInput = (timestamp: number): string => {
    // Create a local date from UTC timestamp to avoid timezone shifts
    const utcDate = new Date(timestamp);
    const localDate = new Date(
      utcDate.getUTCFullYear(),
      utcDate.getUTCMonth(),
      utcDate.getUTCDate()
    );
    return format(localDate, 'yyyy-MM-dd');
  };

  // Simple DOM portal directly to body
  return ReactDOM.createPortal(
    <>
      {/* Toast notification positioned at the top of the page and above the modal */}
      {showToast && (
        <div className="fixed top-4 left-0 right-0 flex justify-center z-[200]">
          <Toast
            message="Reset to defaults"
            onClose={() => setShowToast(false)}
          />
        </div>
      )}
      
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-[100]"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div 
        ref={modalRef}
        className="fixed z-[100] bg-white rounded-lg shadow-xl w-full max-w-xl"
        style={{
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          maxHeight: '85vh'
        }}
        onClick={(e) => e.stopPropagation()} // Prevent clicks from closing the modal
      >
        <div className="flex flex-col h-full max-h-[85vh]">
          {/* Header */}
          <div className="p-6 bg-gray-50 border-b border-gray-200 flex justify-between items-center rounded-t-lg">
            <h2 className="text-xl font-semibold text-gray-900">Inputs</h2>
            <button
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200"
              onClick={onClose}
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Scrollable Content Area */}
          <div className="p-6 overflow-y-auto flex-1">
            <p className="text-gray-600 mb-6">
              Configure your inputs and preferences. Changes are applied immediately. The calculation objective is disabled.
            </p>

            <div className="space-y-8">
              {/* Financial Inputs */}
              <div className="space-y-6">
                <div className="space-y-7">
                  {VARIABLE_ORDER.map(type => {
                    const variable = values[type];
                    const { disabled, message, color, bgColor, borderColor } = getVariableStatus(type);
                    
                    // Custom label for interest rate
                    const label = type === 'interest' ? 'Mortgage Interest Rate' : variable.label;
                    
                    // For date variable, use native date picker
                    if (type === 'date') {
                      return (
                        <div key={type} className="space-y-1">
                          <div className="block text-sm mb-0.5 transition-colors text-gray-600">
                            {label}
                          </div>
                          {/* When date is the calculation objective, show "See results" */}
                          {type === target ? (
                            <div className="w-full p-2 border border-gray-300 rounded text-gray-500 bg-gray-50">
                              See results
                            </div>
                          ) : (
                            <input
                              type="date"
                              value={formatDateForInput(variable.value)}
                              onChange={handleDateChange}
                              min={minDate}
                              max={maxDateStr}
                              className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary text-gray-900"
                            />
                          )}
                          {disabled && message && (
                            <div className={cn(
                              "flex items-center gap-2 mt-2 text-sm px-2 py-1 rounded-md",
                              bgColor, borderColor
                            )}>
                              <Info className={cn("w-4 h-4 flex-shrink-0", color)} />
                              <span className={color}>{message}</span>
                            </div>
                          )}
                        </div>
                      );
                    }
                    
                    // For all other variables, use VariableInput component
                    return (
                      <div key={type} className="space-y-1">
                        <VariableInput
                          type={variable.type}
                          value={variable.value}
                          label={label}
                          onChange={(value) => updateValue(variable.type, value)}
                          disabled={disabled}
                          isCalculationObjective={type === target} // This enables the "See results" text
                          inputClassName="text-lg"
                          min={type === 'income' ? 10000 : undefined}
                          max={
                            type === 'income' ? 9900000 : 
                            type === 'downPayment' ? 9900000 :
                            type === 'savings' ? 999999 :
                            undefined
                          }
                        />
                        {disabled && message && (
                          <div className={cn(
                            "flex items-center gap-2 mt-2 text-sm px-2 py-1 rounded-md",
                            bgColor, borderColor
                          )}>
                            <Info className={cn("w-4 h-4 flex-shrink-0", color)} />
                            <span className={color}>{message}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Mortgage Settings */}
              <div className="space-y-6">
                <div className="space-y-7">
                  <div>
                    <VariableInput
                      type="number"
                      value={settings.amortizationYears}
                      label="Mortgage Term (Years)"
                      onChange={(value) => updateSetting('amortizationYears', value)}
                      inputClassName="text-lg"
                      isCalculationObjective={false}
                    />
                  </div>

                  <div className="pt-4 border-t border-gray-100">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">How We Calculate Your Mortgage</h4>
                    <p className="text-sm text-gray-500">
                      We limit your monthly mortgage payment to {(settings.debtServiceRatio * 100).toFixed(0)}% of your monthly income to ensure affordability.
                      Your maximum mortgage amount is then calculated based on this payment limit, your interest rate,
                      and term. You can adjust this debt service ratio using the slider in the results section.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 sm:p-6 bg-gray-50 border-t border-gray-200 rounded-b-lg">
            <div className="flex justify-between items-center gap-2 sm:gap-4">
              {/* Left side - Reset button */}
              <button
                onClick={handleResetToDefaults}
                className="flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 text-gray-500 bg-white rounded-md border border-gray-200 hover:bg-gray-50 transition-colors text-xs whitespace-nowrap"
              >
                <RefreshCw className="w-3 h-3" />
                <span>Reset</span>
              </button>
              
              {/* Right side - Done button with increased width */}
              <button
                onClick={onClose}
                className="px-5 sm:px-6 py-3 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors text-sm sm:text-base font-medium min-w-[100px] sm:min-w-[120px]"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}